import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, qs, useAction, useApi } from '../api';
import { useCan } from '../auth';
import { Ago, AlertStatus, catLabel, clickableRow, Empty, EntityLink, fmtDate, ListToolbar, MutationError, PageHead, PAGE_SIZE, Pager, plural, Protection, Q, Section, Severity, SortTh, statusText, useFilters } from '../ui/kit';
import { SEVERITY } from '../ui/format';
import { EventTable, useEventDrawer, type EventRow } from './Activity';

const TYPE_LABEL: Record<string, string> = {
  'policy.violation': 'Policy violation',
  'behavior.repeated_blocks': 'Repeated blocked actions',
  'device.protection_lost': 'Protection lost',
  'extension.high_risk_discovered': 'High-risk extension',
  'extension.blocked_present': 'Blocked extension present',
  'extension.permissions_escalated': 'Extension gained high-risk permissions',
  'extension.permissions_added': 'Extension gained permissions',
  'extension.publisher_changed': 'Extension publisher changed',
  'extension.store_removed': 'Extension removed from store',
  'extension.update_source_changed': 'Extension update source changed',
  'extension.listing_changed': 'Extension store listing changed',
  'extension.inventory_flood': 'Unusual extension inventory from a device',
  'extension.known_malicious': 'Known malicious extension',
  'extension.suspicious': 'Suspicious extension',
  'extension.rule_matched': 'Extension rule matched',
  'threat.feed_unhealthy': 'Threat feed unhealthy',
};

const typeLabel = (t: string) => TYPE_LABEL[t] ?? t;

/** True when the alert's type label only repeats what its title already says ("High-risk extension" / "High-risk browser extension discovered: X"). */
export function typeRepeatsTitle(type: string, title: string) {
  const words = typeLabel(type).toLowerCase().split(/[^a-z0-9-]+/).filter((w) => w.length > 3);
  const t = title.toLowerCase();
  return words.length > 0 && words.filter((w) => t.includes(w)).length * 2 >= words.length;
}

/** Severity filter on the shared scale (labels from SEVERITY). */
const SEVERITY_OPTS: Array<[string, string]> = [
  ['', 'Any severity'],
  ['critical', statusText(SEVERITY, 'critical')],
  ['critical,high', `${statusText(SEVERITY, 'high')} or ${statusText(SEVERITY, 'critical').toLowerCase()}`],
  ['high', statusText(SEVERITY, 'high')],
  ['medium', statusText(SEVERITY, 'medium')],
  ['low', statusText(SEVERITY, 'low')],
];

export function Alerts() {
  const filters = useFilters();
  const { sp, set, offset, setOffset } = filters;
  const status = sp.get('status') ?? 'open,investigating';
  const q = useApi<{ total: number; items: any[] }>(`/alerts${qs({ status, severity: sp.get('severity'), q: sp.get('q'), sort: sp.get('sort'), limit: PAGE_SIZE, offset })}`);
  const nav = useNavigate();
  return (
    <>
      <PageHead title="Alerts" sub="Conditions that need a person. Repeated events for the same user, policy and application roll up into one alert until it is closed." />
      <ListToolbar filters={filters} placeholder="Search alerts, users, devices or applications…">
        <select aria-label="Status" value={status} onChange={(e) => set('status', e.target.value)}>
          <option value="open,investigating">Open & investigating</option><option value="open">Open</option><option value="investigating">Investigating</option>
          <option value="resolved,dismissed">Closed</option><option value="open,investigating,resolved,dismissed">All</option>
        </select>
        <select aria-label="Severity" value={sp.get('severity') ?? ''} onChange={(e) => set('severity', e.target.value)}>
          {SEVERITY_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </ListToolbar>
      <Q q={q}>
        {(d) =>
          d.items.length === 0 ? (
            <Empty title="No alerts match">Alerts are raised by policies with “Generate alert”, repeated blocked attempts, lost device heartbeats and risky extensions.</Empty>
          ) : (
            <>
              <div className="table-wrap">
                <table className="t">
                  <thead><tr><th>Severity</th><th>Alert</th><th>Subject</th><th className="num">Events</th><th>Status</th><th>Assignee</th><SortTh filters={filters} col="last_seen_at">Last seen</SortTh></tr></thead>
                  <tbody>
                    {d.items.map((a) => (
                      <tr key={a.id} {...clickableRow(() => nav(`/investigate/alerts/${a.id}`))}>
                        <td><Severity s={a.severity} /></td>
                        <td><EntityLink to={`/investigate/alerts/${a.id}`}>{a.title}</EntityLink>{!typeRepeatsTitle(a.type, a.title) && <div className="sub">{typeLabel(a.type)}</div>}</td>
                        <td>{a.user_name ?? a.device_label ?? <span className="muted">—</span>}{a.app_name && <div className="sub">in {a.app_name}</div>}</td>
                        <td className="num">{a.event_count}</td>
                        <td><AlertStatus s={a.status} /></td>
                        <td className="sub">{a.assignee_email ?? '—'}</td>
                        <td className="sub"><Ago at={a.last_seen_at} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Pager total={d.total} limit={PAGE_SIZE} offset={offset} onChange={setOffset} />
            </>
          )
        }
      </Q>
    </>
  );
}

/** Link to the Exceptions create form, prefilled from an alert (or a policy): `?policy=&user=&app=`. */
export function exceptionLink(p: { policy?: string | null; user?: string | null; app?: string | null }) {
  return `/protect/exceptions${qs({ policy: p.policy, user: p.user, app: p.app })}`;
}

export function AlertDetail() {
  const { id } = useParams();
  const can = useCan();
  const nav = useNavigate();
  const q = useApi<{ alert: any; events: EventRow[]; notes: any[]; assignableAdmins: any[] }>(`/alerts/${id}`);
  const ev = useEventDrawer();
  const [note, setNote] = useState('');
  const [resolution, setResolution] = useState('');
  const update = useAction((b: object) => api(`/alerts/${id}`, { method: 'PATCH', body: b }));
  const addNote = useAction((b: object) => api(`/alerts/${id}/notes`, { method: 'POST', body: b }), () => setNote(''));
  const investigate = useAction(
    (a: any) =>
      api('/investigations', {
        method: 'POST',
        body: {
          title: a.title.slice(0, 200),
          alertIds: [a.id],
          scope: { userId: a.user_id, deviceId: a.user_id ? null : a.device_id, appId: null, from: new Date(Date.parse(a.first_seen_at) - 24 * 3600e3).toISOString(), to: new Date(Math.min(Date.now(), Date.parse(a.last_seen_at) + 24 * 3600e3)).toISOString() },
        },
      }),
    (r: any) => nav(`/investigate/investigations/${r.id}`),
  );
  const writable = can('alerts:write');
  return (
    <Q q={q}>
      {({ alert: a, events, notes, assignableAdmins }) => (
        <>
          <PageHead
            crumbs={<Link to="/investigate/alerts">Alerts</Link>}
            title={a.title}
            sub={a.summary}
            actions={
              <>
                <Severity s={a.severity} /> <AlertStatus s={a.status} />
                {can('exceptions:write') && a.rule_id && <Link className="btn" to={exceptionLink({ policy: a.rule_id, user: a.user_id, app: a.app_id })}>Add exception</Link>}
                {can('investigations:write') && <button className="btn" onClick={() => investigate.mutate(a)} disabled={investigate.isPending}>Open investigation</button>}
              </>
            }
          />
          <MutationError error={update.error ?? investigate.error} />
          <div className="cols cols-2-1">
            <div>
              <dl className="facts">
                <dt>Type</dt><dd>{typeLabel(a.type)}</dd>
                <dt>User</dt><dd>{a.user_id ? <Link to={`/discover/users/${a.user_id}`}>{a.user_name}</Link> : '—'} {a.user_department && <span className="sub">· {a.user_department}</span>}</dd>
                <dt>Device</dt><dd>{a.device_id ? <><Link to={`/discover/devices/${a.device_id}`}>{a.device_label}</Link> <span className="sub">{a.device_managed ? 'managed' : 'unmanaged'}</span> {a.device_state && <Protection s={a.device_state} />}</> : '—'}</dd>
                <dt>Application</dt><dd>{a.app_id ? <Link to={`/discover/apps/${a.app_id}`}>{a.app_name}</Link> : '—'} {a.app_category && <span className="sub">· {catLabel(a.app_category)}</span>}</dd>
                <dt>Policy</dt><dd>{a.rule_id ? <Link to={`/manage/policies/${a.rule_id}`}>{a.rule_name}</Link> : '—'}</dd>
                <dt>First seen</dt><dd>{fmtDate(a.first_seen_at)}</dd>
                <dt>Last seen</dt><dd>{fmtDate(a.last_seen_at)} · {plural(a.event_count, 'event', undefined, String)}</dd>
                {a.resolution && (<><dt>Resolution</dt><dd>{a.resolution}</dd></>)}
              </dl>
              <Section title="Events" aside={events.length < a.event_count ? `showing ${events.length} linked events` : undefined}>
                <EventTable rows={events} onOpen={ev.open} compact />
              </Section>
            </div>
            <div>
              <Section title="Triage">
                {!writable ? (
                  <p className="muted">Your role can view alerts but not change them.</p>
                ) : (
                  <div className="stack-12 mt-12">
                    <label className="field">Assignee
                      <select value={a.assignee_id ?? ''} onChange={(e) => update.mutate({ assigneeId: e.target.value || null })}>
                        <option value="">Unassigned</option>
                        {assignableAdmins.map((x) => <option key={x.id} value={x.id}>{x.name} ({x.email})</option>)}
                      </select>
                    </label>
                    {a.status === 'open' && <button className="btn" onClick={() => update.mutate({ status: 'investigating' })}>Start investigating</button>}
                    {(a.status === 'open' || a.status === 'investigating') && (
                      <>
                        <label className="field">Resolution note <span className="hint">Required to close. Visible in the audit log.</span>
                          <textarea value={resolution} onChange={(e) => setResolution(e.target.value)} maxLength={2000} />
                        </label>
                        <div className="actions">
                          <button className="btn primary" disabled={!resolution.trim()} onClick={() => update.mutate({ status: 'resolved', resolution })}>Resolve</button>
                          <button className="btn" disabled={!resolution.trim()} onClick={() => update.mutate({ status: 'dismissed', resolution })}>Dismiss</button>
                        </div>
                      </>
                    )}
                    {(a.status === 'resolved' || a.status === 'dismissed') && <button className="btn" onClick={() => update.mutate({ status: 'open' })}>Reopen</button>}
                  </div>
                )}
              </Section>
              <Section title="Notes">
                {notes.length === 0 && <p className="muted">No notes yet.</p>}
                {notes.map((n) => (
                  <div className="note" key={n.id}><header>{n.author_email} · <Ago at={n.created_at} /></header>{n.body}</div>
                ))}
                {writable && (
                  <form className="stack-8 mt-12" onSubmit={(e) => { e.preventDefault(); if (note.trim()) addNote.mutate({ body: note }); }}>
                    <textarea aria-label="Add a note" value={note} onChange={(e) => setNote(e.target.value)} maxLength={4000} placeholder="Add a note…" />
                    <div><button className="btn" disabled={!note.trim() || addNote.isPending}>Add note</button></div>
                    <MutationError error={addNote.error} />
                  </form>
                )}
              </Section>
            </div>
          </div>
          {ev.drawer}
        </>
      )}
    </Q>
  );
}
